/**
 * @spec docs/specs/features/staking-ui-batched-reads.md (R9)
 *
 * The client's minBlock floors. After a transaction confirms, every balance of the affected
 * addresses must be read at or after the receipt block. The client keeps the highest unsatisfied
 * floor per chain + address and sends it with every balances fetch, accelerated or ordinary,
 * until a response meets it; values below it stay stale for amount-dependent actions however long
 * that takes. Accelerated retries (~3 s) stop two minutes after the floor was raised; the floor
 * itself stays.
 *
 * Blockless confirmations (no receipt) request a `bypassCache` read per address instead. The
 * request is tracked per value (each asset of a chain, or the R5 total) and acknowledged only by
 * a response that delivered that value fresh (non-stale, with a block): a failed or cancelled
 * fetch, or a 200 carrying a stale/null fallback, leaves it pending. While pending, the value is
 * not fresh for amount-dependent actions, and the read retries every ~3 s for two minutes and on
 * the ordinary poll after that, like an unsatisfied floor. `bypassCache` is sent only for the
 * scopes with a pending value inside that two-minute window, so a chain that stays down cannot
 * drain the upstream budget; after it, the value stays pending until an ordinary read delivers.
 *
 * A floor the server rejects as ahead of its head (400 `MIN_BLOCK_AHEAD_OF_HEAD`) can never be
 * met: the caller drops it and reads once uncached (for every scope of the read), so it is never
 * resent.
 */
import {
  BASE_ASSETS,
  POLYGON_ASSETS,
  isMinBlockRejected,
  type BalancesResponse,
  type Observation,
  type ReadChain,
} from './read-api';

export const ACCELERATED_RETRY_MS = 3_000;
export const ACCELERATION_WINDOW_MS = 2 * 60_000;

/** Floors scoped to the CHIP-redemptions read (R5), which honours minBlock on Base too. */
export type FloorScope = ReadChain | 'redemptions';

/** The single value of the CHIP-redemptions read a bypass request tracks. */
export const REDEMPTIONS_TOTAL = 'total';

/** The values a bypass request for a scope covers. */
const BYPASS_ITEMS: Readonly<Record<FloorScope, readonly string[]>> = {
  base: BASE_ASSETS,
  polygon: POLYGON_ASSETS,
  redemptions: [REDEMPTIONS_TOTAL],
};

/** One pending value of a bypass request, as a fetch saw it when it started. */
export interface BypassEntry {
  readonly scope: FloorScope;
  readonly address: string;
  readonly item: string;
  readonly seq: number;
  /** Client time the request was raised. */
  readonly raisedAt: number;
}

/** The bypass requests a fetch saw when it started, keyed by value. */
export type BypassToken = ReadonlyMap<string, BypassEntry>;

interface Floor {
  readonly block: bigint;
  readonly raisedAt: number;
}

const floors = new Map<string, Floor>();
/** Pending bypass values, each tagged with the request that set it and when. */
const bypass = new Map<string, { readonly seq: number; readonly raisedAt: number }>();
let bypassSeq = 0;
const listeners = new Set<() => void>();
let version = 0;

function key(scope: FloorScope, address: string): string {
  return `${scope}:${address.toLowerCase()}`;
}

function bypassKey(scope: FloorScope, address: string, item: string): string {
  return `${key(scope, address)}:${item}`;
}

function changed(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** useSyncExternalStore plumbing: selectors re-render when a floor is raised or met. */
export function subscribeFloors(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function floorsVersion(): number {
  return version;
}

/** Raise the floor; a lower block never replaces a higher one. */
export function raiseFloor(scope: FloorScope, address: string, block: bigint, now: number): void {
  const k = key(scope, address);
  const current = floors.get(k);
  if (current && current.block >= block) return;
  floors.set(k, { block, raisedAt: now });
  changed();
}

export function floorFor(scope: FloorScope, address: string): bigint | null {
  return floors.get(key(scope, address))?.block ?? null;
}

/** The request's `minBlock`: the highest unsatisfied floor per chain over its addresses. */
export function minBlockFor(addresses: readonly string[]): { base?: number; polygon?: number } {
  const out: { base?: number; polygon?: number } = {};
  for (const chain of ['base', 'polygon'] as const) {
    let highest: bigint | null = null;
    for (const address of addresses) {
      const floor = floorFor(chain, address);
      if (floor !== null && (highest === null || floor > highest)) highest = floor;
    }
    if (highest !== null) out[chain] = Number(highest);
  }
  return out;
}

/** Ask for an uncached read of every value of `scope` for these addresses. */
export function requestBypass(
  scope: FloorScope,
  addresses: readonly string[],
  now: number = Date.now()
): void {
  if (addresses.length === 0) return;
  bypassSeq += 1;
  for (const address of addresses) {
    for (const item of BYPASS_ITEMS[scope]) {
      bypass.set(bypassKey(scope, address, item), { seq: bypassSeq, raisedAt: now });
    }
  }
  changed();
}

/** Whether an uncached read of this value is still owed (it is then not fresh). */
export function isBypassPending(scope: FloorScope, address: string, item: string): boolean {
  return bypass.has(bypassKey(scope, address, item));
}

/** The pending bypass values for these scopes and addresses, without consuming them. */
export function pendingBypass(
  scopes: readonly FloorScope[],
  addresses: readonly string[]
): BypassToken {
  const token = new Map<string, BypassEntry>();
  for (const scope of scopes) {
    for (const address of addresses) {
      for (const item of BYPASS_ITEMS[scope]) {
        const k = bypassKey(scope, address, item);
        const pending = bypass.get(k);
        if (pending) token.set(k, { scope, address, item, ...pending });
      }
    }
  }
  return token;
}

/**
 * Acknowledge the values a response delivered fresh. A value it did not deliver, or one
 * requested again since the fetch started, stays pending.
 */
export function clearBypass(token: BypassToken, delivered: (entry: BypassEntry) => boolean): void {
  let any = false;
  for (const [k, entry] of token) {
    if (bypass.get(k)?.seq === entry.seq && delivered(entry)) {
      bypass.delete(k);
      any = true;
    }
  }
  if (any) changed();
}

/** A value read upstream for this response: available, not stale, with a block. */
export function isDeliveredFresh(obs: Observation | undefined): boolean {
  return !!obs && !obs.stale && obs.value !== null && obs.blockNumber !== null;
}

/** Drop these addresses' floors for a scope, e.g. after the server rejected them. */
export function dropFloors(scope: FloorScope, addresses: readonly string[]): void {
  let any = false;
  for (const address of addresses) {
    if (floors.delete(key(scope, address))) any = true;
  }
  if (any) changed();
}

export interface FlooredReadRequest<T> {
  /** The bypass scopes this read can acknowledge. */
  readonly bypass: readonly FloorScope[];
  readonly addresses: readonly string[];
  /** The floor scopes the request's `minBlock` came from; dropped if the server rejects it. */
  readonly floorScopes: readonly FloorScope[];
  /** Whether the response delivered this pending value fresh. */
  readonly delivered: (result: T, entry: BypassEntry) => boolean;
}

/** The scopes a read should bypass the cache for: pending values raised within the window. */
export function bypassScopes(token: BypassToken, now: number): ReadonlySet<FloorScope> {
  const scopes = new Set<FloorScope>();
  for (const entry of token.values()) {
    if (now - entry.raisedAt < ACCELERATION_WINDOW_MS) scopes.add(entry.scope);
  }
  return scopes;
}

/**
 * One read that carries the floors and any pending bypass request. `bypass` is the set of
 * scopes to read uncached. Pending values are acknowledged only when a read delivers them fresh,
 * whether or not that read bypassed the cache. If the server rejects the floors as ahead of its
 * head, they are dropped (so no later fetch resends them) and the read is retried exactly once,
 * uncached for every scope of the read and without floors; a failure of that retry propagates.
 */
export async function readHonouringFloors<T>(
  request: FlooredReadRequest<T>,
  read: (options: {
    readonly withFloors: boolean;
    readonly bypass: ReadonlySet<FloorScope>;
  }) => Promise<T>
): Promise<T> {
  const token = pendingBypass(request.bypass, request.addresses);
  let result: T;
  try {
    result = await read({ withFloors: true, bypass: bypassScopes(token, Date.now()) });
  } catch (error) {
    if (!isMinBlockRejected(error)) throw error;
    for (const scope of request.floorScopes) dropFloors(scope, request.addresses);
    result = await read({ withFloors: false, bypass: new Set(request.bypass) });
  }
  clearBypass(token, (entry) => request.delivered(result, entry));
  return result;
}

/**
 * Drop every floor the response meets: every asset of that chain for the address read at or
 * above it. A null or failed asset (no block) never satisfies a floor.
 */
export function settleFloors(response: BalancesResponse): void {
  let any = false;
  for (const [address, balances] of Object.entries(response.byAddress)) {
    for (const chain of ['base', 'polygon'] as const) {
      const floor = floorFor(chain, address);
      if (floor === null) continue;
      const observations: readonly Observation[] = Object.values(balances[chain]);
      const met = observations.every((obs) => obs.blockNumber !== null && obs.blockNumber >= floor);
      if (observations.length > 0 && met) {
        floors.delete(key(chain, address));
        any = true;
      }
    }
  }
  if (any) changed();
}

/** Drop a CHIP-redemptions floor once a response at or above it arrives. */
export function settleRedemptionFloor(address: string, blockNumber: bigint | null): void {
  const floor = floorFor('redemptions', address);
  if (floor === null || blockNumber === null || blockNumber < floor) return;
  floors.delete(key('redemptions', address));
  changed();
}

/**
 * True while any of these addresses has a floor, or a pending bypass value, raised within the
 * acceleration window.
 */
export function isAccelerating(
  scope: FloorScope,
  addresses: readonly string[],
  now: number
): boolean {
  const recent = (raisedAt: number): boolean => now - raisedAt < ACCELERATION_WINDOW_MS;
  return addresses.some((address) => {
    const floor = floors.get(key(scope, address));
    if (floor && recent(floor.raisedAt)) return true;
    return BYPASS_ITEMS[scope].some((item) => {
      const pending = bypass.get(bypassKey(scope, address, item));
      return !!pending && recent(pending.raisedAt);
    });
  });
}

/** Test isolation only. */
export function resetFloors(): void {
  floors.clear();
  bypass.clear();
  changed();
}
